import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { cloneDeep } from "lodash";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { ProgressSpinner } from "primereact/progressspinner";
import { Utility } from "../../core/Utility";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getWarehouseOverviewByProductReportTemplate } from "../../document-templates/WarehouseOverviewByProductReportTemplate";
import { WarehouseOverviewByProductsReport } from "../../models/Report";
import { RoleType } from "../../models/Role";
import { Warehouse } from "../../models/Warehouse";
import { Product } from "../../models/Product";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { getAllDocuments } from "../../services/NewVersionService";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { activeUser } from "../../services/AuthService";
import "./WarehouseOverviewByProductsReportPage.scss";

export const WarehouseOverviewByProductsReportPage = () => {
  const { state } = useApplicationContext();
  const { t } = useTranslation();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31, 23, 59, 59);
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // za tekuću godinu – od 1. januara do danas
      const from = new Date(today.getFullYear(), 0, 1, 0, 0, 1);
      const to = Utility.clampDateToRange(today, yearStart, yearEnd);
      return { from, to };
    } else {
      // za ostale godine – cela poslovna godina
      return {
        from: yearStart,
        to: yearEnd,
      };
    }
  };
  const [dateFrom, setDateFrom] = useState<Date>(
    getDefaultRangeForBusinessYear().from
  );
  const [dateTo, setDateTo] = useState<Date>(
    getDefaultRangeForBusinessYear().to
  );

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>(
    activeUser?.role?.name === RoleType.Warehouseman
      ? (activeUser?.warehouse as Warehouse)
      : ({} as Warehouse)
  );
  const [productType, setProductType] = useState<string>("Merkantila");
  const [selectedProductGroup, setSelectedProductGroup] = useState<string>("");
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);

  const [mercantileOverview, setMercantileOverview] = useState<
    WarehouseOverviewByProductsReport[]
  >([]);
  const [totalInputNeto, setTotalInputNeto] = useState<number>(0);
  const [totalInputSrps, setTotalInputSrps] = useState<number>(0);
  const [totalOutputNeto, setTotalOutputNeto] = useState<number>(0);
  const [totalOutputSrps, setTotalOutputSrps] = useState<number>(0);
  const [repromaterialOverview, setRepromaterialOverview] = useState<
    WarehouseOverviewByProductsReport[]
  >([]);

  const [changeState, setChangeState] = useState<boolean>(false);
  const [processing, setProcessing] = useState(false);

  const [pdfData, setPdfData] = useState<any>(
    getWarehouseOverviewByProductReportTemplate(
      mercantileOverview,
      selectedWarehouse.id,
      dateFrom,
      dateTo,
      productType,
      totalInputNeto,
      totalInputSrps,
      totalOutputNeto,
      totalOutputSrps
    )
  );
  const [graphData, setGraphData] = useState<any>({
    labels: [],
    datasets: [
      {
        label: t("Ulaz robe"),
        backgroundColor: "#198754",
        data: [],
      },
      {
        label: t("Ulaz robe Srps"),
        backgroundColor: "#A9E1C5",
        data: [],
      },
      {
        label: t("Izlaz robe"),
        backgroundColor: "#495057",
        data: [],
      },
      {
        label: t("Izlaz robe Srps"),
        backgroundColor: "#6C757D",
        data: [],
      },
    ],
  });
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR")
      throw new Error(resp?.data?.message ?? "Greška");
  };

  useEffect(() => {
    //get data from server
    const run = async () => {
      setProcessing(true);
      try {
        const [wResp, pResp] = await Promise.all([
          getAllWarehouses(),
          getAllProducts(),
        ]);
        [wResp, pResp].forEach(ensureSuccess);
        setWarehousesApi(wResp?.data?.result ?? []);
        setProductsApi(pResp?.data?.result ?? []);
        setChangeState((prev) => !prev);
      } catch (e) {
        console.log(e);
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, []);

  useEffect(() => {
    //reset states on filter change
    setMercantileOverview([]);
    setRepromaterialOverview([]);
    setProcessing(true);
    setChangeState(!changeState);
  }, [selectedWarehouse, dateFrom, dateTo, productType, selectedProductGroup]);

  useEffect(() => {
    // set data for mercantile and repromaterial overview tables
    const run = async () => {
      await formatMercantileOverviewData();
      await formatRepromaterialOverviewData();
      setProcessing(false);
    };
    run();
  }, [changeState]);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
  }, [businessYear]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getWarehouseOverviewByProductReportTemplate(
      productType === "Merkantila" ? mercantileOverview : repromaterialOverview,
      warehousesApi.find((item) => item.id === selectedWarehouse.id)
        ?.name as string,
      dateFrom,
      dateTo,
      productType,
      totalInputNeto,
      totalInputSrps,
      totalOutputNeto,
      totalOutputSrps
    );
    setPdfData(newPdf);
  }, [mercantileOverview, repromaterialOverview]);

  const formatMercantileOverviewData = async () => {
    const warehouse = warehousesApi.find(
      (item) => item.id == selectedWarehouse.id
    );
    const warehouseName = warehouse?.name;
    // ako magacin nije izabran – nema šta da računamo
    if (!warehouseName) {
      setMercantileOverview([]);
      setTotalInputNeto(0);
      setTotalInputSrps(0);
      setTotalOutputNeto(0);
      setTotalOutputSrps(0);
      return;
    }

    let copyOfMercantileOverview = [];

    // dobijanje ulaza i izlazea posebno
    const [inputsResponse, outputsResponse]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, dateFrom, dateTo), // prijemnice merkantile
        getAllDocuments(true, false, dateFrom, dateTo), // otpremnice merkantile
      ]);
    const inputDocs = inputsResponse.data.result;
    const outputDocs = outputsResponse.data.result;

    // ULAZI – aggregacija po DANU + GRUPI (kategoriji)
    for (const mercantileInput of inputDocs) {
      if (
        mercantileInput?.isDocumentFinished !== true || // samo zaključeni
        mercantileInput.warehouseName !== warehouseName || // izabrani magacin
        (selectedProductGroup !== "" &&
          mercantileInput.categoryName !== selectedProductGroup) // opcioni filter po grupi
      ) {
        continue;
      }

      const docDate = new Date(mercantileInput.date);
      const idx = copyOfMercantileOverview.findIndex((item) => {
        const d = new Date(item.date);
        return (
          d.getFullYear() === docDate.getFullYear() &&
          d.getMonth() === docDate.getMonth() &&
          d.getDate() === docDate.getDate() &&
          item.groupName === mercantileInput.categoryName
        );
      });

      if (idx >= 0) {
        copyOfMercantileOverview[idx].importAmount +=
          mercantileInput.netto ?? 0;
        copyOfMercantileOverview[idx].importAmountSrps =
          (copyOfMercantileOverview[idx].importAmountSrps ?? 0) +
          (mercantileInput.srps ?? 0);
      } else {
        copyOfMercantileOverview.push({
          id: mercantileInput.id,
          groupName: mercantileInput.categoryName as string,
          date: docDate,
          importAmount: mercantileInput.netto ?? 0,
          importAmountSrps: mercantileInput.srps ?? 0,
          exportAmount: 0,
          exportAmountSrps: 0,
        });
      }
    }

    // IZLAZI – aggregacija po DANU + GRUPI (kategoriji)
    for (const mercantileOutput of outputDocs) {
      if (
        mercantileOutput?.isDocumentFinished !== true ||
        mercantileOutput.warehouseName !== warehouseName ||
        (selectedProductGroup !== "" &&
          mercantileOutput.categoryName !== selectedProductGroup)
      ) {
        continue;
      }

      const docDate = new Date(mercantileOutput.date);
      const idx = copyOfMercantileOverview.findIndex((item) => {
        const d = new Date(item.date);
        return (
          d.getFullYear() === docDate.getFullYear() &&
          d.getMonth() === docDate.getMonth() &&
          d.getDate() === docDate.getDate() &&
          item.groupName === mercantileOutput.categoryName
        );
      });

      if (idx >= 0) {
        copyOfMercantileOverview[idx].exportAmount +=
          mercantileOutput.netto ?? 0;
        copyOfMercantileOverview[idx].exportAmountSrps =
          (copyOfMercantileOverview[idx].exportAmountSrps ?? 0) +
          (mercantileOutput.srps ?? 0);
      } else {
        copyOfMercantileOverview.push({
          id: mercantileOutput.id,
          groupName: mercantileOutput.categoryName as string,
          date: docDate,
          importAmount: 0,
          importAmountSrps: 0,
          exportAmount: mercantileOutput.netto ?? 0,
          exportAmountSrps: mercantileOutput.srps ?? 0,
        });
      }
    }

    copyOfMercantileOverview.sort(
      (
        a: WarehouseOverviewByProductsReport,
        b: WarehouseOverviewByProductsReport
      ) => {
        return new Date(a.date) < new Date(b.date) ? 1 : -1;
      }
    );

    //izracunavanje ukupne neto i srps vrednosti
    let newTotalInputNeto: number = 0;
    let newTotalInputSrps: number = 0;
    let newTotalOutputNeto: number = 0;
    let newTotalOutputSrps: number = 0;
    for (let i = 0; i < copyOfMercantileOverview.length; i++) {
      newTotalInputNeto += copyOfMercantileOverview[i].importAmount;
      newTotalInputSrps += copyOfMercantileOverview[i]?.importAmountSrps ?? 0;
      newTotalOutputNeto += copyOfMercantileOverview[i].exportAmount;
      newTotalOutputSrps += copyOfMercantileOverview[i]?.exportAmountSrps ?? 0;
    }
    setTotalInputNeto(newTotalInputNeto);
    setTotalInputSrps(newTotalInputSrps);
    setTotalOutputNeto(newTotalOutputNeto);
    setTotalOutputSrps(newTotalOutputSrps);

    //create graph data
    const newGraphData = cloneDeep(graphData);
    newGraphData.labels = [];
    newGraphData.datasets[0].data = [];
    newGraphData.datasets[1].data = [];
    newGraphData.datasets[2].data = [];
    newGraphData.datasets[3].data = [];
    for (const item of copyOfMercantileOverview) {
      newGraphData.labels.unshift(Utility.formatDate(new Date(item.date)));
      newGraphData.datasets[0].data.unshift(item.importAmount);
      newGraphData.datasets[1].data.unshift(item.importAmountSrps);
      newGraphData.datasets[2].data.unshift(item.exportAmount);
      newGraphData.datasets[3].data.unshift(item.exportAmountSrps);
    }
    setGraphData(newGraphData);

    setMercantileOverview(copyOfMercantileOverview);
  };

  const formatRepromaterialOverviewData = async () => {
    const warehouse = warehousesApi.find(
      (item) => item.id === selectedWarehouse.id
    );
    const warehouseName = warehouse?.name;
    if (!warehouseName) {
      setRepromaterialOverview([]);
      return;
    }
    const dateFromFormated = new Date(dateFrom).getTime();
    const dateToFormated = new Date(dateTo).getTime() + 1000 * 60 * 60 * 24; //kraj dana
    let copyOfRepromaterialOverview: any[] = [];

    // dobijanje ulaza i izlaza repromaterijala
    const [inputsResponse, outputsResponse]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(false, true, dateFrom, dateTo), // repromaterijal - prijemnice
        getAllDocuments(false, false, dateFrom, dateTo), // repromaterijal - otpremnice
      ]);

    const inputDocs = inputsResponse.data.result;
    const outputDocs = outputsResponse.data.result;

    // ULAZI REPROMATERIJALA
    for (const repromaterialInput of inputDocs) {
      if (
        repromaterialInput?.isDocumentFinished !== true || // zaključen dokument
        repromaterialInput.warehouseName !== warehouseName || // izabrani magacin
        new Date(repromaterialInput.date).getTime() <= dateFromFormated ||
        new Date(repromaterialInput.date).getTime() > dateToFormated
      ) {
        continue;
      }

      const idsRaw = repromaterialInput?.reproProducts ?? "";
      const amountsRaw = repromaterialInput?.reproAmounts ?? "";

      const productIds = idsRaw.split(";").filter(Boolean);
      const amounts = amountsRaw
        .split(";")
        .filter(Boolean)
        .map(
          (chunk: string) =>
            // izvuče leading broj (podrška i za zarez kao decimalni separator)
            parseFloat(chunk.replace(",", ".").replace(/[^\d.-]/g, "")) || 0
        );

      const docDate = new Date(repromaterialInput.date);

      productIds.forEach((prodId: string, index: string | number) => {
        const productMeta = productsApi.find((item) => item.id === prodId);
        if (!productMeta) return;

        const productCategoryId = productMeta.category?.id;

        // filter po grupi (HILF/PFLA/... => '1','2','3','4','5')
        if (
          selectedProductGroup !== "" &&
          parseInt(productCategoryId as string) !==
            parseInt(selectedProductGroup as string)
        ) {
          return;
        }

        const amount = amounts[index] ?? 0;
        const productName = productMeta.name as string;
        const groupName = productMeta.category?.name as string;

        const idx = copyOfRepromaterialOverview.findIndex((repromaterial) => {
          const d = new Date(repromaterial.date);
          return (
            d.getFullYear() === docDate.getFullYear() &&
            d.getMonth() === docDate.getMonth() &&
            d.getDate() === docDate.getDate() &&
            repromaterial.productName === productName
          );
        });

        if (idx >= 0) {
          copyOfRepromaterialOverview[idx].importAmount += amount;
        } else {
          copyOfRepromaterialOverview.push({
            id: prodId,
            groupName,
            categoryGroup: productCategoryId,
            productName,
            date: docDate,
            importAmount: amount,
            exportAmount: 0,
          });
        }
      });
    }

    // IZLAZI REPROMATERIJALA
    for (const repromaterialOutput of outputDocs) {
      if (
        repromaterialOutput?.isDocumentFinished !== true ||
        repromaterialOutput.warehouseName !== warehouseName ||
        new Date(repromaterialOutput.date).getTime() <= dateFromFormated ||
        new Date(repromaterialOutput.date).getTime() > dateToFormated
      ) {
        continue;
      }

      const idsRaw = repromaterialOutput?.reproProducts ?? "";
      const amountsRaw = repromaterialOutput?.reproAmounts ?? "";

      const productIds = idsRaw.split(";").filter(Boolean);
      const amounts = amountsRaw
        .split(";")
        .filter(Boolean)
        .map(
          (chunk: string) =>
            parseFloat(chunk.replace(",", ".").replace(/[^\d.-]/g, "")) || 0
        );

      const docDate = new Date(repromaterialOutput.date);

      productIds.forEach((prodId: string, index: string | number) => {
        const productMeta = productsApi.find((item) => item.id === prodId);
        if (!productMeta) return;

        const productCategoryId = productMeta?.category?.id;

        if (
          selectedProductGroup !== "" &&
          parseInt(productCategoryId as string) !==
            parseInt(selectedProductGroup as string)
        ) {
          return;
        }

        const amount = amounts[index] ?? 0;
        const productName = productMeta?.name as string;
        const groupName = productMeta?.category?.name as string;

        const idx = copyOfRepromaterialOverview.findIndex((repromaterial) => {
          const d = new Date(repromaterial.date);
          return (
            d.getFullYear() === docDate.getFullYear() &&
            d.getMonth() === docDate.getMonth() &&
            d.getDate() === docDate.getDate() &&
            repromaterial.productName === productName
          );
        });

        if (idx >= 0) {
          copyOfRepromaterialOverview[idx].exportAmount += amount;
        } else {
          copyOfRepromaterialOverview.push({
            id: prodId,
            groupName,
            categoryGroup: productCategoryId,
            productName,
            date: docDate,
            importAmount: 0,
            exportAmount: amount,
          });
        }
      });
    }

    const filteredCopyOfRepromaterialOverview =
      copyOfRepromaterialOverview.filter(
        (oneReprom) =>
          oneReprom.productName !== "" && oneReprom.productName !== undefined
      );

    setRepromaterialOverview(filteredCopyOfRepromaterialOverview);
  };

  const onSelectedWarehouseChange = (property: string, value: string) => {
    let copyOfNewWarehouse = cloneDeep(selectedWarehouse);
    copyOfNewWarehouse = warehousesApi.find(
      (item) => item.id == value
    ) as Warehouse;
    setSelectedWarehouse(copyOfNewWarehouse);
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    if (!value) {
      const { from } = getDefaultRangeForBusinessYear();
      setDateFrom(from);
      return;
    }
    let d = Utility.clampDateToRange(value, yearStart, yearEnd);
    if (d > dateTo) {
      d = dateTo;
    }
    setDateFrom(d);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    if (!value) {
      const { to } = getDefaultRangeForBusinessYear();
      setDateTo(to);
      return;
    }
    let d = Utility.clampDateToRange(value, yearStart, yearEnd);
    if (d < dateFrom) {
      d = dateFrom;
    }
    setDateTo(d);
  };

  const onProductTypeChange = (property: string, value: string) => {
    setProductType(value);
    setSelectedProductGroup("");
  };

  const onSelectedProductGroupChange = (property: string, value: string) => {
    setSelectedProductGroup(value);
  };

  const renderMercantileHeader = () => {
    return (
      <div className="mercantile-header">
        <span>{t("MERKANTILNA ROBA")}</span>
      </div>
    );
  };

  const renderRepromaterialHeader = () => {
    return (
      <div className="repromaterial-header">
        <span>{t("REPROMATERIJAL")}</span>
      </div>
    );
  };

  const formatDate = (rowData: any) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const importAmount = (rowData: WarehouseOverviewByProductsReport) => {
    return Utility.addThousandSeparator(rowData.importAmount);
  };

  const exportAmount = (rowData: WarehouseOverviewByProductsReport) => {
    return Utility.addThousandSeparator(rowData.exportAmount);
  };

  const importAmountSrps = (rowData: WarehouseOverviewByProductsReport) => {
    return Utility.addThousandSeparator(rowData?.importAmountSrps as number);
  };

  const exportAmountSrps = (rowData: WarehouseOverviewByProductsReport) => {
    return Utility.addThousandSeparator(rowData?.exportAmountSrps as number);
  };

  const importReproAmount = (rowData: any) => {
    return !Number.isInteger(rowData.importAmount)
      ? rowData.importAmount.toFixed(3)
      : rowData.importAmount.toFixed(0);
  };

  const exportReproAmount = (rowData: any) => {
    return !Number.isInteger(rowData.exportAmount)
      ? rowData.exportAmount.toFixed(3)
      : rowData.exportAmount.toFixed(0);
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-title">{t("Kartica magacina")}</span>
            <DropdownInput
              id="selectedWarehouse"
              value={selectedWarehouse.id}
              onValueChange={onSelectedWarehouseChange}
              options={warehousesApi.filter((item) => item.virtual !== true)}
              optionValue="id"
              optionLabel="name"
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman || processing
              }
            />
            <span className="header-elements">{t("od")}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("do")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("za tip")}</span>
            <DropdownInput
              id="productType"
              value={productType}
              onValueChange={onProductTypeChange}
              options={[
                { label: t("Merkantila"), value: "Merkantila" },
                { label: t("Repromaterijal"), value: "Repromaterijal" },
              ]}
              labelText=""
              placeholderText=""
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("za grupu")}</span>
            <DropdownInput
              id="selectedProductGroup"
              value={selectedProductGroup}
              onValueChange={onSelectedProductGroupChange}
              options={
                productType === "Merkantila"
                  ? [
                      { label: t("Uljana repica"), value: "Uljana repica" },
                      { label: t("Ječam"), value: "Ječam" },
                      { label: t("Pšenica"), value: "Pšenica" },
                      { label: t("Suncokret"), value: "Suncokret" },
                      { label: t("Soja"), value: "Soja" },
                      { label: t("Kukuruz"), value: "Kukuruz" },
                    ]
                  : [
                      { label: "HILF - Pomoćni materijal", value: "1" },
                      { label: "PFLA - Hemija", value: "2" },
                      { label: "DUMI - Djubriva", value: "3" },
                      { label: "SAAT - Seme", value: "4" },
                      { label: "AGCM - Merkantila", value: "5" },
                    ]
              }
              labelText=""
              placeholderText={t("Grupa robe")}
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
          </div>
        </div>
        {productType === "Merkantila" && (
          <div className="table-body">
            {processing ? (
              <ProgressSpinner
                style={{ width: "100px", height: "100px", color: "green" }}
                strokeWidth="4"
                animationDuration=".8s"
              />
            ) : (
              <DataTable
                header={renderMercantileHeader}
                value={mercantileOverview}
                showGridlines
                stripedRows
              >
                <Column
                  field="groupName"
                  header={t("Grupa robe")}
                  footer={t("Ukupno")}
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
                <Column
                  body={formatDate}
                  header={t("Datum")}
                  footer=""
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
                <Column
                  body={importAmount}
                  header={t("Ulaz robe neto")}
                  footer={Utility.addThousandSeparator(totalInputNeto)}
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
                <Column
                  body={importAmountSrps}
                  header={t("Ulaz robe SRPS")}
                  footer={Utility.addThousandSeparator(totalInputSrps)}
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
                <Column
                  body={exportAmount}
                  header={t("Izlaz robe neto")}
                  footer={Utility.addThousandSeparator(totalOutputNeto)}
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
                <Column
                  body={exportAmountSrps}
                  header={t("Izlaz robe SRPS")}
                  footer={Utility.addThousandSeparator(totalOutputSrps)}
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
              </DataTable>
            )}
          </div>
        )}
        {productType === "Repromaterijal" && (
          <div className="table-body-repromaterial">
            {!processing && (
              <DataTable
                header={renderRepromaterialHeader}
                value={repromaterialOverview}
                showGridlines
                stripedRows
              >
                <Column field="groupName" header={t("Grupa robe")} />
                <Column field="productName" header={t("Naziv robe")} />
                <Column body={formatDate} header={t("Datum")} />
                <Column body={importReproAmount} header={t("Ulaz robe")} />
                <Column body={exportReproAmount} header={t("Izlaz robe")} />
              </DataTable>
            )}
          </div>
        )}
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Kartica_magacina")}_${
              warehousesApi.find((item) => item.id === selectedWarehouse.id)?.name
            }_${Utility.formatDate(new Date())}.pdf`}
          />
        </div>
        {mercantileOverview.length > 0 && productType === "Merkantila" && (
          <div className="card">
            <Chart type="bar" data={graphData} />
          </div>
        )}
      </div>
    </PageLayout>
  );
};
